/**
 * Your travel report from your own log (website roadmap S29 — torn.report's travel page): your flights, the trips they
 * make (out to a country, there, back), what you bought abroad, worth and profit, per country, per item and per day. On
 * the log-report kit (log-kit.ts; the browser half is apps/web/src/app/log-kit/). Pure and browser-safe; tested in
 * packages/shared/test/travel-report.test.ts.
 *
 * Torn doesn't document its travel log lines, so they are read leniently and nothing is guessed:
 * - What a line is comes from its title (and its category): a flight leaving ("Travel initiate", "… depart", "… take
 *   off", "… return"), a landing ("… arrive", "… land"), a buy abroad ("… abroad buy", "Travel … buy/purchase"). Any
 *   other line in Torn's Travel category is kept as "other", shown as it is.
 * - Where: `destination`/`to`/`country`/`area`… and `origin`/`from`… — a country's name (or its three-letter code) is
 *   matched to Torn's eleven destinations; "Torn"/"Torn City" is home; a number is kept as "Place #n" (Torn's country ids
 *   aren't known here). A "… return" line with no destination means flying back to Torn (that's what the title says).
 * - Items: `items` (or `item` + `quantity`), price from `cost_each` or the total over the amount; ticket from
 *   `cost`/`ticket`; flight time from `duration`/`flight_time`; the flight type from `travel_method`/`method`/`type`.
 *   What a line doesn't say stays unknown (null).
 * - A buy that doesn't say where is put in the country of the flight you were on (flew there, not yet flown back) —
 *   marked "from your flight"; with no such flight, the country stays unknown.
 * - Worth and profit are ESTIMATES: what you bought, at Torn's market value today (not what you sold it for), less what
 *   you paid and the tickets your log shows.
 * Nothing here goes anywhere: the website keeps the log in the browser and asks Torn with the player's own key.
 */
import { DEFAULT_AGENCY } from './abroad';
import { logItems, type LogLike } from './crimes-report';
import { inRange, sumPerDay, type DateRange } from './log-kit';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const posInt = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const first = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
  return undefined;
};

// ---------------------------------------------------------------- places

/** A place a line names: one of Torn's destinations (key = its code), home ('torn'), or something else as given. */
export interface Place {
  key: string;
  name: string;
}

export const HOME: Place = { key: 'torn', name: 'Torn' };

const ALIASES: Record<string, string> = {
  uk: 'uni',
  'united kingdom': 'uni',
  england: 'uni',
  britain: 'uni',
  'great britain': 'uni',
  cayman: 'cay',
  'cayman islands': 'cay',
  'united arab emirates': 'uae',
  emirates: 'uae',
  dubai: 'uae',
  'south africa': 'sou',
  'south-africa': 'sou',
  sa: 'sou',
  switzerland: 'swi',
  argentina: 'arg',
  hawaii: 'haw',
  japan: 'jap',
  china: 'chi',
  canada: 'can',
  mexico: 'mex',
};

/** A line's place → one of Torn's destinations, home, or the text/number as given (never a guessed country). */
export function placeOf(x: unknown): Place | null {
  const o = obj(x);
  if (o) return placeOf(first(o, ['name', 'title', 'country', 'destination', 'id']));
  const n = posInt(x);
  if (n !== null) return { key: `#${n}`, name: `Place #${n}` };
  const s = str(x);
  if (!s) return null;
  const k = s.toLowerCase().replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^torn( city)?$/.test(k) || k === 'home' || k === 'tc') return HOME;
  const code = ALIASES[k] ?? DEFAULT_AGENCY.countries.find((c) => c.code === k || c.name.toLowerCase() === k)?.code ?? null;
  if (code) return { key: code, name: DEFAULT_AGENCY.countries.find((c) => c.code === code)!.name };
  return { key: `?${k}`, name: s };
}

const isHome = (p: Place | null): boolean => p?.key === HOME.key;

// ---------------------------------------------------------------- one line

export type TravelKind = 'depart' | 'arrive' | 'buy' | 'other';

export interface TravelBuy {
  /** The log line's id (+ `#n` for its later items). */
  id: string;
  at: number;
  itemId: number;
  qty: number;
  /** Price of one (null: the line doesn't say). */
  each: number | null;
  /** What it cost in all (null: unknown). */
  total: number | null;
  /** Where (null: unknown). */
  place: Place | null;
  /** Where came from: the line itself, or the flight you were on. */
  placeFrom: 'line' | 'flight' | null;
}

export interface TravelEvent {
  id: string;
  at: number;
  kind: TravelKind;
  title: string;
  /** Where to (a flight leaving) or where (a landing, a buy). */
  to: Place | null;
  /** Where from (a flight leaving). */
  from: Place | null;
  /** Flight type as the line says it ("Standard", "Airstrip"…). */
  method: string | null;
  /** The ticket, when the line says it. */
  cost: number | null;
  /** Flight time in seconds, when the line says it. */
  duration: number | null;
  /** Items bought abroad (a buy line). */
  buys: TravelBuy[];
}

/** What a line is, from its title and category (null: not a travel line). */
export function travelKind(l: Pick<LogLike, 'title' | 'category'>): TravelKind | null {
  const t = l.title.trim().toLowerCase();
  const cat = /^travel/i.test((l.category ?? '').trim());
  const travelish = cat || /\b(travel|flight|abroad)\b/.test(t);
  if (!travelish) return null;
  if (/\babroad\b/.test(t) && /\b(buy|bought|purchas)/.test(t)) return 'buy';
  if (/\b(buy|bought|purchas)/.test(t)) return cat || /\b(travel|abroad)/.test(t) ? 'buy' : null;
  if (/\b(arriv|land)/.test(t)) return 'arrive';
  if (/\b(initiat|depart|take ?off|took off|begin|start|return|fly|flew|leave|left)/.test(t)) return 'depart';
  return cat ? 'other' : null;
}

/** A log line → the travel event in it (null when it isn't a travel line). */
export function readTravelLine(l: LogLike): TravelEvent | null {
  const kind = travelKind(l);
  if (!kind) return null;
  const d = l.data ?? {};
  const title = l.title.trim();
  let to = placeOf(first(d, ['destination', 'destination_id', 'to', 'country', 'area', 'location', 'arrived', 'place']));
  const from = placeOf(first(d, ['origin', 'origin_id', 'from', 'departure', 'departed', 'source']));
  if (!to && kind === 'depart' && /\breturn|\bhome\b/i.test(title)) to = HOME;
  const method = str(first(d, ['travel_method', 'method', 'travel_type', 'flight_type', 'class', 'type']));
  const cost = kind === 'buy' ? null : num(first(d, ['cost', 'ticket', 'ticket_cost', 'ticket_price', 'price']));
  const durRaw = num(first(d, ['duration', 'flight_time', 'travel_time']));
  const duration = kind === 'depart' || kind === 'arrive' ? (durRaw !== null && durRaw > 0 ? durRaw : null) : null;
  let buys: TravelBuy[] = [];
  if (kind === 'buy') {
    const rawItems = first(d, ['items', 'item', 'items_bought', 'item_id']);
    let items = logItems(rawItems).filter((i) => Number.isInteger(i.id) && i.amount > 0);
    const qtySaid = posInt(first(d, ['quantity', 'qty', 'amount']));
    if (items.length === 1 && qtySaid && num(rawItems) !== null) items = [{ id: items[0]!.id, amount: qtySaid }];
    const units = items.reduce((s, i) => s + i.amount, 0);
    const totalSaid = num(first(d, ['cost_total', 'total_cost', 'total', 'total_price', 'cost']));
    const eachSaid = num(first(d, ['cost_each', 'price_each', 'price']));
    const each = eachSaid !== null && eachSaid >= 0 ? eachSaid : totalSaid !== null && units ? totalSaid / units : null;
    buys = items.map((it, n) => ({
      id: n ? `${l.id}#${n}` : l.id,
      at: l.at,
      itemId: it.id,
      qty: it.amount,
      each,
      total: items.length === 1 && totalSaid !== null ? totalSaid : each !== null ? each * it.amount : null,
      place: to,
      placeFrom: to ? 'line' : null,
    }));
  }
  return { id: l.id, at: l.at, kind, title, to, from, method, cost, duration, buys };
}

/** Every travel event in a set of log lines, oldest first. */
export function readTravelLog(lines: readonly LogLike[]): TravelEvent[] {
  return lines.map(readTravelLine).filter((e): e is TravelEvent => !!e).sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}

// ---------------------------------------------------------------- trips

/** One trip: out to a country, (landed), what you bought, (flew back), (home). Parts the log doesn't show are null. */
export interface Trip {
  /** The outbound flight's line id (or the first line seen of the trip). */
  id: string;
  country: Place | null;
  /** Left Torn. */
  leftAt: number | null;
  /** Landed there. */
  landedAt: number | null;
  /** Flew back. */
  backAt: number | null;
  /** Home in Torn. */
  homeAt: number | null;
  /** Tickets the log shows (out and back). */
  tickets: number;
  method: string | null;
  buys: TravelBuy[];
  /** Flight time out, seconds (the line's own, else landing − leaving). */
  flightOut: number | null;
  /** Flight time back, seconds. */
  flightBack: number | null;
}

/** When a trip counts as happening (for ranges and per-day sums). */
export const tripAt = (t: Trip): number => t.leftAt ?? t.landedAt ?? t.buys[0]?.at ?? t.backAt ?? t.homeAt ?? 0;

/** Seconds spent there (landed → flew back), when the log shows both. */
export const timeThere = (t: Trip): number | null => (t.landedAt !== null && t.backAt !== null && t.backAt >= t.landedAt ? Math.round((t.backAt - t.landedAt) / 1000) : null);

export interface TravelHistory {
  trips: Trip[];
  /** Every flight that left (out and back) — for flights per day. */
  flights: TravelEvent[];
  /** Every buy abroad, its place filled from the flight when the line doesn't say. */
  buys: TravelBuy[];
  /** Travel lines that are neither a flight, a landing nor a buy (shown as they are). */
  other: TravelEvent[];
}

/**
 * The events (oldest first) → trips. A flight to a country starts a trip; landing there, buying, flying back and landing
 * home fill it in. A trip whose start is before the kept log (a landing or a flight back first) starts there.
 */
export function buildTrips(events: readonly TravelEvent[]): TravelHistory {
  const trips: Trip[] = [];
  const flights: TravelEvent[] = [];
  const buys: TravelBuy[] = [];
  const other: TravelEvent[] = [];
  let cur: Trip | null = null;
  const start = (id: string, country: Place | null): Trip => {
    const t: Trip = { id, country, leftAt: null, landedAt: null, backAt: null, homeAt: null, tickets: 0, method: null, buys: [], flightOut: null, flightBack: null };
    trips.push(t);
    return t;
  };
  const open = (): Trip | null => (cur && cur.homeAt === null ? cur : null);
  for (const e of events) {
    if (e.kind === 'other') {
      other.push(e);
      continue;
    }
    if (e.kind === 'depart') {
      flights.push(e);
      const back = isHome(e.to) || (!e.to && !!e.from && !isHome(e.from));
      if (e.to && !isHome(e.to)) {
        // Out to a country: a new trip.
        cur = start(e.id, e.to);
        cur.leftAt = e.at;
        cur.method = e.method;
        cur.tickets += e.cost ?? 0;
        cur.flightOut = e.duration;
      } else if (!e.to && isHome(e.from)) {
        // Out of Torn to somewhere the line doesn't name: a trip whose country a landing or a buy may tell.
        cur = start(e.id, null);
        cur.leftAt = e.at;
        cur.method = e.method;
        cur.tickets += e.cost ?? 0;
        cur.flightOut = e.duration;
      } else if (back) {
        const t: Trip = open() && open()!.backAt === null ? open()! : (cur = start(e.id, e.from && !isHome(e.from) ? e.from : null));
        if (!t.country && e.from && !isHome(e.from)) t.country = e.from;
        t.backAt = e.at;
        t.tickets += e.cost ?? 0;
        t.flightBack = e.duration;
      }
      // A flight that says neither where to nor where from: counted as a flight only.
      continue;
    }
    if (e.kind === 'arrive') {
      const place = e.to;
      const t = open();
      if (isHome(place) || (!place && t && t.backAt !== null)) {
        if (t) {
          t.homeAt = e.at;
          if (t.flightBack === null && t.backAt !== null) t.flightBack = e.duration ?? Math.round((e.at - t.backAt) / 1000);
        }
        cur = null;
      } else if (t && t.landedAt === null && t.backAt === null && (!place || !t.country || place.key === t.country.key)) {
        if (!t.country) t.country = place;
        t.landedAt = e.at;
        if (t.flightOut === null && t.leftAt !== null) t.flightOut = e.duration ?? Math.round((e.at - t.leftAt) / 1000);
      } else if (place) {
        // Landed abroad with no flight out kept: the trip starts here.
        cur = start(e.id, place);
        cur.landedAt = e.at;
        cur.flightOut = e.duration;
      }
      continue;
    }
    // A buy abroad: in the trip you're on when it's there (or doesn't say where).
    const t = open();
    const there = t && t.backAt === null ? t : null;
    for (const b of e.buys) {
      const buy: TravelBuy = { ...b };
      if (!buy.place && there?.country) {
        buy.place = there.country;
        buy.placeFrom = 'flight';
      }
      if (there && (!buy.place || !there.country || buy.place.key === there.country.key)) {
        if (!there.country) there.country = buy.place;
        there.buys.push(buy);
      }
      buys.push(buy);
    }
  }
  return { trips, flights, buys, other };
}

// ---------------------------------------------------------------- sums

/** An item's market value today (null: unknown). */
export type ValueOf = (itemId: number) => number | null;

export interface BuySums {
  units: number;
  /** Paid for the units whose price is known. */
  spent: number;
  /** Units bought without a known price. */
  unpriced: number;
  /** Their market value today (units with a known value). */
  worth: number;
  /** Units without a known value today. */
  unvalued: number;
  /** Worth − spent over the units both are known for (null: none). */
  profit: number | null;
}

/** What a set of buys cost and is worth today (estimated profit only where both are known). */
export function sumBuys(buys: readonly TravelBuy[], valueOf: ValueOf): BuySums {
  const s: BuySums = { units: 0, spent: 0, unpriced: 0, worth: 0, unvalued: 0, profit: null };
  let profit = 0;
  let any = false;
  for (const b of buys) {
    s.units += b.qty;
    const v = valueOf(b.itemId);
    if (b.total === null) s.unpriced += b.qty;
    else s.spent += b.total;
    if (v === null) s.unvalued += b.qty;
    else s.worth += v * b.qty;
    if (b.total !== null && v !== null) {
      profit += v * b.qty - b.total;
      any = true;
    }
  }
  s.profit = any ? Math.round(profit) : null;
  s.spent = Math.round(s.spent);
  s.worth = Math.round(s.worth);
  return s;
}

export interface TravelTotals extends BuySums {
  trips: number;
  flights: number;
  /** Tickets the log shows. */
  tickets: number;
  /** Estimated profit less the tickets (null: no profit known). */
  net: number | null;
  /** Seconds in the air the log shows (flights with a known time). */
  airSec: number;
  /** Seconds spent abroad (trips with both landing and flying back). */
  thereSec: number;
}

/** Trips, flights and buys in a range → the tiles. */
export function travelTotals(h: TravelHistory, range: DateRange, valueOf: ValueOf): TravelTotals {
  const trips = h.trips.filter((t) => inRange(tripAt(t), range));
  const buys = h.buys.filter((b) => inRange(b.at, range));
  const s = sumBuys(buys, valueOf);
  const tickets = trips.reduce((a, t) => a + t.tickets, 0);
  return {
    ...s,
    trips: trips.length,
    flights: h.flights.filter((f) => inRange(f.at, range)).length,
    tickets,
    net: s.profit === null ? null : s.profit - tickets,
    airSec: trips.reduce((a, t) => a + (t.flightOut ?? 0) + (t.flightBack ?? 0), 0),
    thereSec: trips.reduce((a, t) => a + (timeThere(t) ?? 0), 0),
  };
}

export interface CountryRow extends BuySums {
  place: Place | null;
  trips: number;
  tickets: number;
  net: number | null;
  /** Average time there over the trips that show it (seconds; null: none). */
  avgThere: number | null;
  last: number;
}

/** Per country: trips, buys, spent, worth, estimated profit — most trips first. Buys outside a trip count for their place. */
export function byCountry(h: TravelHistory, range: DateRange, valueOf: ValueOf): CountryRow[] {
  const keyOf = (p: Place | null) => p?.key ?? '';
  const groups = new Map<string, { place: Place | null; trips: Trip[]; buys: TravelBuy[] }>();
  const g = (p: Place | null) => {
    let x = groups.get(keyOf(p));
    if (!x) groups.set(keyOf(p), (x = { place: p, trips: [], buys: [] }));
    return x;
  };
  for (const t of h.trips) if (inRange(tripAt(t), range)) g(t.country).trips.push(t);
  for (const b of h.buys) if (inRange(b.at, range)) g(b.place).buys.push(b);
  return [...groups.values()]
    .map((x) => {
      const s = sumBuys(x.buys, valueOf);
      const tickets = x.trips.reduce((a, t) => a + t.tickets, 0);
      const there = x.trips.map(timeThere).filter((v): v is number => v !== null);
      return {
        ...s,
        place: x.place,
        trips: x.trips.length,
        tickets,
        net: s.profit === null ? null : s.profit - tickets,
        avgThere: there.length ? Math.round(there.reduce((a, b) => a + b, 0) / there.length) : null,
        last: Math.max(0, ...x.trips.map(tripAt), ...x.buys.map((b) => b.at)),
      };
    })
    .sort((a, b) => b.trips - a.trips || b.units - a.units || (a.place?.name ?? '~').localeCompare(b.place?.name ?? '~'));
}

export interface ItemRow extends BuySums {
  itemId: number;
  /** Average paid for one (null: no price known). */
  avgCost: number | null;
  /** Value of one today (null: unknown). */
  value: number | null;
  /** Where it was bought. */
  places: Place[];
  last: number;
}

/** Per item bought abroad, most spent first. */
export function byItem(buys: readonly TravelBuy[], range: DateRange, valueOf: ValueOf): ItemRow[] {
  const by = new Map<number, TravelBuy[]>();
  for (const b of buys) if (inRange(b.at, range)) by.set(b.itemId, [...(by.get(b.itemId) ?? []), b]);
  return [...by.entries()]
    .map(([itemId, list]) => {
      const s = sumBuys(list, valueOf);
      const priced = list.filter((b) => b.total !== null).reduce((a, b) => a + b.qty, 0);
      const places = new Map<string, Place>();
      for (const b of list) if (b.place) places.set(b.place.key, b.place);
      return { ...s, itemId, avgCost: priced ? Math.round(s.spent / priced) : null, value: valueOf(itemId), places: [...places.values()], last: Math.max(...list.map((b) => b.at)) };
    })
    .sort((a, b) => b.spent - a.spent || b.units - a.units);
}

/** Flights that left, per Torn day in the range. */
export function flightsPerDay(h: TravelHistory, range: DateRange, now: number): Array<{ at: number; v: number }> {
  return sumPerDay(h.flights.filter((f) => inRange(f.at, range)), () => 1, range, now);
}

/** Estimated profit of what you bought abroad (less tickets), per Torn day in the range. */
export function profitPerDay(h: TravelHistory, range: DateRange, now: number, valueOf: ValueOf): Array<{ at: number; v: number }> {
  const pts: Array<{ at: number; v: number }> = [];
  for (const b of h.buys) {
    const v = valueOf(b.itemId);
    if (inRange(b.at, range) && v !== null && b.total !== null) pts.push({ at: b.at, v: v * b.qty - b.total });
  }
  for (const t of h.trips) if (t.tickets && inRange(tripAt(t), range)) pts.push({ at: tripAt(t), v: -t.tickets });
  return sumPerDay(pts, (p) => p.v, range, now);
}

// ---------------------------------------------------------------- CSV

const iso = (ms: number | null) => (ms === null ? '' : new Date(ms).toISOString());

/** One row per trip. */
export function tripsCsvRows(trips: readonly Trip[], valueOf: ValueOf, name: (id: number) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  const header = ['left_utc', 'landed_utc', 'flew_back_utc', 'home_utc', 'country', 'flight_type', 'flight_out_s', 'flight_back_s', 'time_there_s', 'tickets', 'items', 'units', 'spent', 'worth_now_est', 'profit_est'];
  const rows = trips.map((t) => {
    const s = sumBuys(t.buys, valueOf);
    return [iso(t.leftAt), iso(t.landedAt), iso(t.backAt), iso(t.homeAt), t.country?.name ?? '', t.method ?? '', t.flightOut, t.flightBack, timeThere(t), t.tickets, t.buys.map((b) => `${b.qty}x ${name(b.itemId)}`).join('; '), s.units, s.spent, s.worth, s.profit === null ? null : s.profit - t.tickets];
  });
  return { header, rows };
}

/** One row per item bought abroad. */
export function buysCsvRows(buys: readonly TravelBuy[], valueOf: ValueOf, name: (id: number) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  const header = ['time_utc', 'country', 'country_from', 'item_id', 'item', 'quantity', 'price_each', 'total', 'value_each_now', 'profit_est'];
  const rows = buys.map((b) => {
    const v = valueOf(b.itemId);
    return [iso(b.at), b.place?.name ?? '', b.placeFrom ?? '', b.itemId, name(b.itemId), b.qty, b.each, b.total, v, v !== null && b.total !== null ? Math.round(v * b.qty - b.total) : null];
  });
  return { header, rows };
}
